import { Component, ElementRef, inject, ViewChild } from '@angular/core';

import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';
import { AuthService } from '@/services/auth.service';
import { TrpcService } from '@/services/trpc.service';
import { FeatureFlagService } from '@/services/feature-flag.service';
import { CommonService } from '@/services/common.service';

@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [AppMenuitem, RouterModule],
    template: `<div class="layout-menu-container" #menuContainer>
        <ul class="layout-menu">
            @for (item of model; track item; let i = $index) {

                @if (!item.separator) {
<li app-menuitem [item]="item" [index]="i" [root]="true"></li>
}
                @if (item.separator) {
<li class="menu-separator"></li>
}

}
        </ul>
    </div>`
})
export class AppMenu {
    el: ElementRef = inject(ElementRef);

    @ViewChild('menuContainer') menuContainer!: ElementRef;
    model: MenuItem[] = [];

    adminMenuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'pi pi-home',
            routerLink: ['/admin/dashboard']
        },
        // APEX CRM Menu Items
        {
            label: 'Accounts',
            icon: 'pi pi-building',
            items: [
                {
                    label: 'All Accounts',
                    icon: 'pi pi-list',
                    routerLink: ['/admin/apex/accounts']
                },
                {
                    label: 'Sage Export',
                    icon: 'pi pi-download',
                    routerLink: ['/admin/apex/accounts/export']
                },
            ]
        },
        {
            label: 'Contractors',
            icon: 'pi pi-wrench',
            routerLink: ['/admin/apex/contractors']
        },
        {
            label: 'Contacts',
            icon: 'pi pi-user',
            routerLink: ['/admin/apex/contacts'],
            visible: false
        },
        {
            label: 'Products',
            icon: 'pi pi-box',
            routerLink: ['/admin/apex/products']
        },
        {
            label: 'Opportunities',
            icon: 'pi pi-dollar',
            routerLink: ['/admin/apex/opportunities']
        },
        {
            label: 'Proposals',
            icon: 'pi pi-file',
            routerLink: ['/admin/apex/proposals']
        },
        // {
        //     label: 'Jobs',
        //     icon: 'pi pi-briefcase',
        //     routerLink: ['/admin/apex/jobs']
        // },
        {
            label: 'Invoices',
            icon: 'pi pi-receipt',
            items: [
                {
                    label: 'All Invoices',
                    icon: 'pi pi-list',
                    routerLink: ['/admin/apex/invoices']
                },
                {
                    label: 'Sage Export',
                    icon: 'pi pi-download',
                    routerLink: ['/admin/apex/invoices/export']
                },
                {
                    label: 'Import Payments',
                    icon: 'pi pi-upload',
                    routerLink: ['/admin/apex/invoices/import-payments']
                }
            ]
        },
        {
            label: 'Proposal Statuses',
            icon: 'pi pi-list',
            routerLink: ['/admin/apex/proposal-statuses']
        },
        {
            label: 'Proposal Templates',
            icon: 'pi pi-file-edit',
            routerLink: ['/admin/apex/proposal-templates']
        },
        // End APEX CRM Menu Items
        {
            label: 'Account Requests',
            icon: 'pi pi-users',
            routerLink: ['/admin/account-requests']
        }, {
            label: 'User Login Management',
            icon: 'pi pi-key',
            routerLink: ['/admin/manage-logins']
        }, {
            label: '',
            icon: 'pi pi-cog',
            items: [
                {
                    label: 'Companies',
                    icon: 'pi pi-building',
                    routerLink: ['/admin/settings/companies']
                },
                {
                    label: 'Global Settings',
                    icon: 'pi pi-sliders-h',
                    routerLink: ['/admin/settings']
                },
                {
                    label: 'Invoice Settings',
                    icon: 'pi pi-receipt',
                    routerLink: ['/admin/invoice-setting']
                },
                {
                    label: 'System Status',
                    icon: 'pi pi-heart',
                    routerLink: ['/admin/system-status']
                },
                {
                    label: 'Email Logs',
                    icon: 'pi pi-envelope',
                    routerLink: ['/admin/email-logs']
                }
            ]
        }, {
            label: 'ACH Deposit Reconciliation',
            icon: 'pi pi-dollar',
            routerLink: ['/admin/ach-deposit-reconciliation']
        }
    ];

    clientMenuItems: MenuItem[] = [
        {
            label: 'Dashboard',
            icon: 'pi pi-home',
            routerLink: ['/client/dashboard']
        }, {
            label: 'View Invoices',
            icon: 'pi pi-receipt',
            routerLink: ['/client/invoice-list']
        }, {
            label: 'View Proposals',
            icon: 'pi pi-file',
            routerLink: ['/client/proposals']
        }, {
            label: 'Account Settings',
            icon: 'pi pi-cog',
            routerLink: ['/client/account-settings']
        }
    ];

    accountingMenuItems: MenuItem[] = [
        {
            label: 'Generate Report',
            icon: 'pi pi-clipboard',
            routerLink: ['/accounting/general-report']
        },
        {
            label: 'ACH Deposit Reconciliation',
            icon: 'pi pi-dollar',
            routerLink: ['/accounting/ach-deposit-reconciliation']
        }, {
            label: 'Email Logs',
            icon: 'pi pi-envelope',
            routerLink: ['/accounting/email-logs']
        }
    ];

    constructor(
        private authService: AuthService,
        private trpcService: TrpcService,
        private featureFlagService: FeatureFlagService,
        private commonService: CommonService
    ) {


    }

    async ngOnInit() {

        let currentUser = this.authService.getCurrentUser();

        // Filter menu items based on feature flags
        const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
            if (this.featureFlagService.isAchEnabled()) {
                return items; // Show all items if ACH is enabled
            }

            // Filter out ACH-related menu items
            return items.filter(item => {
                const routerLink = item.routerLink?.[0];
                return routerLink !== '/admin/ach-deposit-reconciliation' &&
                    routerLink !== '/accounting/ach-deposit-reconciliation';
            });
        };

        if (currentUser?.role == "admin") {
            this.model = filterMenuItems(this.adminMenuItems);
        } else if (currentUser?.role == "client") {
            this.model = this.clientMenuItems;
        } else if (currentUser?.role == "manager") {
            this.model = filterMenuItems([
                ...this.adminMenuItems,
                ...this.accountingMenuItems
            ]);
        }

        // Inject developer menu when flag is enabled (admin/manager only)
        if (currentUser?.role == "admin" || currentUser?.role == "manager") {
            if (this.featureFlagService.isDevMenuEnabled()) {
                this.model.push({
                    label: 'Developer',
                    icon: 'pi pi-code',
                    items: [
                        { label: 'Developer Settings', icon: 'pi pi-sliders-h', routerLink: ['/admin/dev/settings'] },
                        { label: 'Proposal Tokens', icon: 'pi pi-key', routerLink: ['/admin/dev/tokens'] }
                    ]
                });
            }
        }

        this.model.push({
            label: 'Logout',
            icon: 'pi pi-sign-out',
            routerLink: ['/logout']
        });
    }
}
